/**
 * Mein Profil — persönliche Einstellungen des eingeloggten Mitarbeiters:
 * - Signatur für E-Mails (HTML)
 * - Signatur für Schriftsätze/Briefe (Text)
 * - Absender-Postfach für Microsoft-Versand (Shared Mailbox)
 * - „Systemvorschlag laden"-Button pro Rolle (E-Mail + Brief)
 *
 * Route: /mein-profil (alle eingeloggten Nutzer)
 */
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { User, PaperPlaneTilt, EnvelopeSimple, Note, MagicWand, FloppyDisk, MicrosoftOutlookLogo, Key, ShieldCheck, Camera, Signature, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import MicrosoftIntegrationPanel from "@/components/MicrosoftIntegrationPanel";

export default function MeinProfil() {
  const { user, refresh: refreshUser } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    extension: "",
    position: "",
    code: "",
    signature_email: "",
    signature_letter: "",
    signature_block: "",
    signature_png_base64: "",
    send_from_shared_mailbox: "",
    reply_to_email: "",
    language: "de",
    timezone: "Europe/Berlin",
  });
  const [suggestions, setSuggestions] = useState(null);
  const [saving, setSaving] = useState(false);
  const avatarFileRef = useRef(null);
  const sigFileRef = useRef(null);

  // Konto-Tab (E-Mail + Passwort)
  const [currentEmail, setCurrentEmail] = useState("");
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailSaving, setEmailSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", new_password_confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    api.get("/users/me").then(({ data }) => {
      setMe(data);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone: data.phone || "",
        extension: data.extension || "",
        position: data.position || "",
        salutation: data.salutation || "",
        code: data.code || "",
        signature_email: data.signature_email || "",
        signature_letter: data.signature_letter || "",
        signature_block: data.signature_block || "",
        signature_png_base64: data.signature_png_base64 || "",
        send_from_shared_mailbox: data.send_from_shared_mailbox || "",
        reply_to_email: data.reply_to_email || "",
        language: data.language || "de",
        timezone: data.timezone || "Europe/Berlin",
      });
      setCurrentEmail(data.email || "");
    });
    api.get("/users/me/signature-suggestions").then(({ data }) => setSuggestions(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMe((m) => ({ ...m, avatar_url: r.data.avatar_url, avatar_updated_at: Date.now() }));
      await refreshUser?.();
      toast.success("Profilbild aktualisiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const uploadSignaturePng = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bild-Dateien (PNG/JPG) sind erlaubt");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 2 MB)");
      return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      set("signature_png_base64", dataUrl);
      toast.success("Signatur geladen — nicht vergessen zu speichern!");
    } catch (err) {
      toast.error("Konnte Signatur nicht laden");
    }
  };
  const clearSignaturePng = () => set("signature_png_base64", "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      toast.success("Profil gespeichert");
      await refreshUser?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const loadEmailSuggestion = () => {
    if (!suggestions?.email_html) return;
    set("signature_email", suggestions.email_html);
    toast.info(`Systemvorschlag für Rolle „${suggestions.position}" geladen`);
  };
  const loadLetterSuggestion = () => {
    if (!suggestions?.letter_text) return;
    set("signature_letter", suggestions.letter_text);
    toast.info(`Systemvorschlag für Rolle „${suggestions.position}" geladen`);
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.current_password) return;
    setEmailSaving(true);
    try {
      const { data } = await api.patch("/users/me/email", emailForm);
      toast.success(`E-Mail geändert: ${data.email}`);
      setCurrentEmail(data.email);
      setEmailForm({ new_email: "", current_password: "" });
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "E-Mail-Änderung fehlgeschlagen");
    } finally {
      setEmailSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.new_password_confirm) {
      toast.error("Neues Passwort und Bestätigung stimmen nicht überein");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: pwForm.old_password,
        new_password: pwForm.new_password,
      });
      toast.success("Passwort geändert");
      setPwForm({ old_password: "", new_password: "", new_password_confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Passwort-Änderung fehlgeschlagen");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="mein-profil-page">
      <div className="flex items-center gap-3 mb-6">
        <User size={26} className="text-slate-700" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mein Profil</h1>
          <p className="text-sm text-slate-500">
            Persönliche Signaturen, Absender-Einstellungen und Integrationen für <strong>{user?.name}</strong> ({suggestions?.position || user?.role}).
          </p>
        </div>
      </div>

      <Tabs defaultValue="account">
        <TabsList>
          <TabsTrigger value="account" data-testid="mp-tab-account"><ShieldCheck size={14} className="mr-1.5" />Konto</TabsTrigger>
          <TabsTrigger value="signatures" data-testid="mp-tab-signatures"><Note size={14} className="mr-1.5" />Signaturen</TabsTrigger>
          <TabsTrigger value="mail" data-testid="mp-tab-mail"><EnvelopeSimple size={14} className="mr-1.5" />Absender-Konto</TabsTrigger>
          <TabsTrigger value="microsoft" data-testid="mp-tab-microsoft"><MicrosoftOutlookLogo size={14} className="mr-1.5" />Outlook-Verknüpfung</TabsTrigger>
        </TabsList>

        {/* --- Konto: Persönliche Daten + E-Mail + Passwort --- */}
        <TabsContent value="account" className="pt-4 space-y-5">
          {/* Profilfoto + Stammdaten */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><User size={16} /> Persönliche Daten</h3>
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                {me?.avatar_url ? (
                  <img
                    src={me.avatar_url.startsWith("http")
                      ? `${me.avatar_url}${me.avatar_url.includes("?") ? "&" : "?"}t=${me.avatar_updated_at || Date.now()}`
                      : `${process.env.REACT_APP_BACKEND_URL || ""}${me.avatar_url}?t=${me.avatar_updated_at || Date.now()}`}
                    alt="Profilbild"
                    className="h-24 w-24 rounded-full object-cover border border-stone-200"
                    data-testid="mp-avatar-img"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-stone-200 flex items-center justify-center text-3xl font-semibold text-slate-600" data-testid="mp-avatar-fallback">
                    {((form.first_name?.[0] || me?.name?.[0] || "?") + (form.last_name?.[0] || "")).toUpperCase()}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => avatarFileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 bg-slate-900 text-white rounded-full p-2 hover:bg-slate-700"
                  title="Profilbild ändern"
                  data-testid="mp-avatar-upload-btn"
                >
                  <Camera size={16} />
                </button>
                <input type="file" ref={avatarFileRef} accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="mp-avatar-input" />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Anrede</Label>
                  <Select value={form.salutation || "none"} onValueChange={(v) => set("salutation", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="mp-salutation"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— keine —</SelectItem><SelectItem value="herr">Herr</SelectItem><SelectItem value="frau">Frau</SelectItem><SelectItem value="divers">Divers (ohne Anrede)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div />
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Vorname</Label>
                  <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="mp-first-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nachname</Label>
                  <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="mp-last-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Telefon</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+49 …" data-testid="mp-phone" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Durchwahl</Label>
                  <Input value={form.extension} onChange={(e) => set("extension", e.target.value)} data-testid="mp-extension" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Position / Rolle</Label>
                  <Input value={form.position} onChange={(e) => set("position", e.target.value)} placeholder="z. B. Geschäftsführung" data-testid="mp-position" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Kürzel (Mein Zeichen)</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => set("code", e.target.value.toUpperCase())}
                    placeholder={((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "z. B. MR"}
                    maxLength={6}
                    className="font-mono uppercase"
                    data-testid="mp-code"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Antwortadresse (Reply-To)</Label>
                <Input type="email" value={form.reply_to_email} onChange={(e) => set("reply_to_email", e.target.value)} placeholder="antwort@meinedomain.de" data-testid="mp-reply-to" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Sprache</Label>
                <Select value={form.language} onValueChange={(v) => set("language", v)}>
                  <SelectTrigger data-testid="mp-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Zeitzone</Label>
                <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                  <SelectTrigger data-testid="mp-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe/Berlin">Europa/Berlin (MEZ)</SelectItem>
                    <SelectItem value="Europe/Vienna">Europa/Wien</SelectItem>
                    <SelectItem value="Europe/Zurich">Europa/Zürich</SelectItem>
                    <SelectItem value="Europe/London">Europa/London (GMT)</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save-account">
                <FloppyDisk size={14} className="mr-2" /> {saving ? "Speichere…" : "Persönliche Daten speichern"}
              </Button>
            </div>
          </div>

          {/* E-Mail + Passwort */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><EnvelopeSimple size={16} /> E-Mail-Adresse ändern</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ihre aktuelle Anmeldeadresse: <span className="font-mono text-slate-800">{currentEmail || "—"}</span>. Zur Bestätigung wird Ihr aktuelles Passwort benötigt.
                </p>
              </div>
              <form onSubmit={changeEmail} className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Neue E-Mail-Adresse</Label>
                  <Input
                    type="email" required autoComplete="email"
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))}
                    placeholder="neue.adresse@ihre-firma.de"
                    data-testid="mp-new-email"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Aktuelles Passwort</Label>
                  <Input
                    type="password" required autoComplete="current-password"
                    value={emailForm.current_password}
                    onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))}
                    data-testid="mp-email-current-pw"
                  />
                </div>
                <Button type="submit" disabled={emailSaving} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save-email">
                  <FloppyDisk size={14} className="mr-2" /> {emailSaving ? "Speichere…" : "E-Mail ändern"}
                </Button>
              </form>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Key size={16} /> Passwort ändern</h3>
                <p className="text-xs text-slate-500 mt-0.5">Mindestens 6 Zeichen. Danach müssen andere Sessions ggf. neu angemeldet werden.</p>
              </div>
              <form onSubmit={changePassword} className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Aktuelles Passwort</Label>
                  <Input
                    type="password" required autoComplete="current-password"
                    value={pwForm.old_password}
                    onChange={(e) => setPwForm((f) => ({ ...f, old_password: e.target.value }))}
                    data-testid="mp-pw-old"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Neues Passwort</Label>
                  <Input
                    type="password" required minLength={6} autoComplete="new-password"
                    value={pwForm.new_password}
                    onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                    data-testid="mp-pw-new"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Neues Passwort wiederholen</Label>
                  <Input
                    type="password" required minLength={6} autoComplete="new-password"
                    value={pwForm.new_password_confirm}
                    onChange={(e) => setPwForm((f) => ({ ...f, new_password_confirm: e.target.value }))}
                    data-testid="mp-pw-new-confirm"
                  />
                </div>
                <Button type="submit" disabled={pwSaving} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save-pw">
                  <FloppyDisk size={14} className="mr-2" /> {pwSaving ? "Speichere…" : "Passwort ändern"}
                </Button>
              </form>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="signatures" className="pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* E-Mail-Signatur */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><EnvelopeSimple size={16} /> E-Mail-Signatur</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Erscheint am Ende jeder aus Domivus versendeten E-Mail. HTML erlaubt.</p>
                </div>
                <Button variant="outline" size="sm" onClick={loadEmailSuggestion} data-testid="mp-load-email-suggestion">
                  <MagicWand size={13} className="mr-1.5" /> Vorschlag laden
                </Button>
              </div>
              <Textarea
                rows={10}
                value={form.signature_email}
                onChange={(e) => set("signature_email", e.target.value)}
                placeholder="Wenn leer, wird eine Signatur automatisch aus Ihrem Namen + Firmen-Stammdaten erzeugt."
                className="font-mono text-xs"
                data-testid="mp-signature-email"
              />
              {/* Live-Preview */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Vorschau</Label>
                <div
                  className="mt-1 p-3 border border-slate-200 rounded bg-slate-50 text-sm text-slate-800 leading-relaxed min-h-[80px]"
                  dangerouslySetInnerHTML={{ __html: form.signature_email || "<span style='color:#94a3b8;font-style:italic'>Signatur wird automatisch aus Stammdaten erzeugt.</span>" }}
                  data-testid="mp-signature-email-preview"
                />
              </div>
            </div>

            {/* Brief-Signatur */}
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><PaperPlaneTilt size={16} /> Brief-/Schriftsatz-Signatur</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Wird unter dem Anschreiben im PDF eingefügt. Reiner Text, mit Leerzeilen für Unterschrift.</p>
                </div>
                <Button variant="outline" size="sm" onClick={loadLetterSuggestion} data-testid="mp-load-letter-suggestion">
                  <MagicWand size={13} className="mr-1.5" /> Vorschlag laden
                </Button>
              </div>
              <Textarea
                rows={10}
                value={form.signature_letter}
                onChange={(e) => set("signature_letter", e.target.value)}
                placeholder="Mit freundlichen Grüßen&#10;&#10;&#10;Max Mustermann&#10;Objektverwaltung"
                className="font-mono text-xs whitespace-pre"
                data-testid="mp-signature-letter"
              />
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Vorschau (wie im Brief)</Label>
                <pre className="mt-1 p-3 border border-slate-200 rounded bg-slate-50 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap min-h-[80px]" data-testid="mp-signature-letter-preview">
                  {form.signature_letter || "(Standard-Grußformel wird automatisch aus Ihrem Namen + Rolle erzeugt.)"}
                </pre>
              </div>
            </div>
          </div>

          {/* 2026-02 (feb) — Digitale PNG-Unterschrift für amtliche Dokumente */}
          <div className="mt-5 bg-white border border-slate-200 rounded-lg p-5 space-y-3">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Signature size={16} /> Digitale Unterschrift (PNG)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Wird auf amtliche Dokumente (Wohnungsgeberbestätigung, Vermieterbescheinigung, …) über die Unterschriftslinie gelegt.
                Empfehlung: transparenter PNG, ca. 300×80 px, max. 2 MB.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-72 h-28 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
                {form.signature_png_base64 ? (
                  <img src={form.signature_png_base64} alt="Signatur" className="max-h-24 max-w-64 object-contain" data-testid="mp-signature-png-preview" />
                ) : (
                  <span className="text-xs text-slate-400">Keine Signatur hochgeladen</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" onClick={() => sigFileRef.current?.click()} data-testid="mp-signature-png-upload">
                  <Camera size={14} className="mr-1.5" /> {form.signature_png_base64 ? "Ersetzen" : "Hochladen"}
                </Button>
                {form.signature_png_base64 && (
                  <Button variant="ghost" size="sm" onClick={clearSignaturePng} data-testid="mp-signature-png-clear" className="text-red-600 hover:text-red-800">
                    <X size={14} className="mr-1.5" /> Entfernen
                  </Button>
                )}
                <input type="file" ref={sigFileRef} accept="image/png,image/jpeg" className="hidden" onChange={uploadSignaturePng} data-testid="mp-signature-png-input" />
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save-signatures">
              <FloppyDisk size={14} className="mr-2" /> {saving ? "Speichere…" : "Signaturen speichern"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="mail" className="pt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 max-w-2xl space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900">Absender-Postfach</h3>
              <p className="text-xs text-slate-500 mt-1">
                Falls Sie nicht aus Ihrem persönlichen Postfach senden möchten, sondern aus einem <strong>Shared Mailbox</strong> (z.B. <code>info@ihre-firma.de</code>), tragen Sie diese Adresse hier ein.
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Shared Mailbox (Absender-Adresse)</Label>
              <Input
                type="email"
                value={form.send_from_shared_mailbox}
                onChange={(e) => set("send_from_shared_mailbox", e.target.value)}
                placeholder="info@ihre-firma.de"
                data-testid="mp-shared-mailbox"
              />
            </div>
            <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3 space-y-1.5">
              <p><strong>Voraussetzungen (einmalig in Microsoft 365):</strong></p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Ein Admin muss Ihnen im <strong>Exchange Admin Center</strong> die Berechtigung <strong>&bdquo;Send As&ldquo;</strong> (Senden als) für dieses Postfach freischalten.</li>
                <li>Nach dem Speichern hier bitte <strong>Outlook-Verknüpfung einmal trennen und neu verbinden</strong> (im Tab &bdquo;Outlook-Verknüpfung&ldquo;), damit die neue Berechtigung <code>Mail.Send.Shared</code> mit gewährt wird.</li>
                <li>Ist das Feld leer, wird Ihre persönliche M365-Adresse als Absender verwendet.</li>
              </ol>
            </div>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-save-mailbox">
              <FloppyDisk size={14} className="mr-2" /> {saving ? "Speichere…" : "Absender speichern"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="microsoft" className="pt-4">
          <div className="max-w-2xl">
            <MicrosoftIntegrationPanel />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
